import { useCallback, useState } from "react";
import * as Yup from "yup";

/**
 * Holds per-field error messages for a form whose validity is managed by
 * `useFormHandlers` (which exposes `invalidFields` but not the messages).
 *
 * Usage:
 *   const { errors, validateField, setFieldErrors, clearErrors } =
 *     useYupFieldErrors<FormData>(schema);
 *   <Input ... onBlur={() => validateField("email", formData)} error={!!errors.email} />
 *
 * `validateField` runs a single-field Yup check and updates `errors[name]`.
 * `setFieldErrors` is useful for hydrating server-side field errors from a
 * 400 response.
 */
export default function useYupFieldErrors<T extends object>(
  schema: Yup.AnyObjectSchema,
) {
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  const validateField = useCallback(
    async (name: keyof T, formData: T) => {
      try {
        await schema.validateAt(name as string, formData as object);
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
      }
    },
    [schema],
  );

  const setFieldErrors = useCallback(
    (next: Partial<Record<keyof T, string>>) => setErrors(next),
    [],
  );

  const clearErrors = useCallback(() => setErrors({}), []);

  /**
   * Convenience: try to extract `{field: message}` errors from a request
   * failure body (DRF returns an object keyed by field). Only keys that match
   * an actual form field (per the Yup schema) are applied — this ignores
   * non-field keys like `error`, `detail`, or `non_field_errors`, which would
   * otherwise be set as invisible errors and swallow the caller's toast.
   * Returns whether any field errors were applied — so the caller can fall
   * back to a toast when the error wasn't field-specific.
   */
  const applyServerFieldErrors = useCallback(
    (body: unknown): boolean => {
      if (!body || typeof body !== "object") return false;
      const schemaFields = schema.fields;
      const next: Partial<Record<keyof T, string>> = {};
      for (const key of Object.keys(body) as Array<keyof T>) {
        if (!(key in schemaFields)) continue;
        const value = (body as Record<string, unknown>)[key as string];
        next[key] = Array.isArray(value) ? String(value[0]) : String(value);
      }
      if (Object.keys(next).length === 0) return false;
      setErrors(next);
      return true;
    },
    [schema],
  );

  return {
    errors,
    validateField,
    setFieldErrors,
    clearErrors,
    applyServerFieldErrors,
  };
}
